import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart, Search, Trash2, Filter, X, ListChecks, CheckCheck, Tag, Plus } from "lucide-react";
import { AnimatePresence, m } from "framer-motion";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import MatchCard from "../components/matches/MatchCard";
import FilterPanel from "../components/swipe/FilterPanel";
import { normalizeCategory } from "@/utils/categoryUtils";
import { normalizeColor, getColorName } from "@/utils/colorUtils";
import { fuzzyMatchProduct } from "@/utils/fuzzySearch";
import { useCategories } from "@/hooks/useCategories";
import SkeletonGrid from "../components/ui/SkeletonGrid";

export default function MatchesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(() => searchParams.get("q") || "");
    const [productToDelete, setProductToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Multi-selection state
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());
    const [showBatchDeleteDialog, setShowBatchDeleteDialog] = useState(false);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    // Fetch user collections
    const { categories: userCategories = [] } = useCategories();
    const uniqueCategoryNames = Array.from(new Set(userCategories.map(c => c.category_name).filter(Boolean)));

    const [filters, setFilters] = useState(() => ({
        minPrice: Number(searchParams.get("min_price")) || 0,
        maxPrice: Number(searchParams.get("max_price")) || 10000,
        categories: [],
        subcategories: searchParams.get("subcategories") ? searchParams.get("subcategories").split(",") : [],
        colors: searchParams.get("colors") ? searchParams.get("colors").split(",") : [],
        brands: searchParams.get("brands") ? searchParams.get("brands").split(",") : [],
        sizes: searchParams.get("sizes") ? searchParams.get("sizes").split(",") : [],
        inStockOnly: searchParams.get("in_stock") === "true",
    }));

    // Sync State -> URL
    useEffect(() => {
        const params = {};
        if (searchQuery) params.q = searchQuery;
        if (filters.minPrice > 0) params.min_price = filters.minPrice.toString();
        if (filters.maxPrice < 10000) params.max_price = filters.maxPrice.toString();
        if (filters.subcategories.length > 0) params.subcategories = filters.subcategories.join(",");
        if (filters.colors.length > 0) params.colors = filters.colors.join(",");
        if (filters.brands.length > 0) params.brands = filters.brands.join(",");
        if (filters.sizes.length > 0) params.sizes = filters.sizes.join(",");
        if (filters.inStockOnly) params.in_stock = "true";

        setSearchParams(params, { replace: true });
    }, [searchQuery, filters, setSearchParams]);

    // Back/Forward Sync
    useEffect(() => {
        const currentParams = Object.fromEntries(searchParams.entries());

        setSearchQuery(currentParams.q || "");
        setFilters(prev => ({
            ...prev,
            minPrice: Number(currentParams.min_price) || 0,
            maxPrice: Number(currentParams.max_price) || 10000,
            subcategories: currentParams.subcategories ? currentParams.subcategories.split(",") : [],
            colors: currentParams.colors ? currentParams.colors.split(",") : [],
            brands: currentParams.brands ? currentParams.brands.split(",") : [],
            sizes: currentParams.sizes ? currentParams.sizes.split(",") : [],
            inStockOnly: currentParams.in_stock === "true",
        }));
    }, [searchParams]);

    const queryClient = useQueryClient();

    const { data: interactions = [], isLoading } = useQuery({
        queryKey: ['interactions'],
        queryFn: async () => {
            try { await apiClient.auth.me(); } catch (e) { /* Guest mode */ }
            // Fetch ALL swipe_right AND like (legacy) interactions
            return apiClient.entities.UserInteraction.list({ action: 'swipe_right,like' });
        },
        refetchOnMount: 'always',
    });

    const likedProductIds = [...new Set(interactions
        .filter(i => ['swipe_right', 'like'].includes(i.action))
        .map(i => i.product_id))];

    const { data: products = [], isLoading: isProductsLoading } = useQuery({
        queryKey: ['products', likedProductIds],
        queryFn: async () => {
            if (likedProductIds.length === 0) return [];
            // Fetch products individually to ensure we have them all
            // Ideally backend supports bulk get, but fallback to parallel requests
            const promises = likedProductIds.map(id =>
                apiClient.entities.Product.get(id).catch(() => null)
            );
            const results = await Promise.all(promises);
            // Filter nulls and ensure uniqueness (in case API returns weird stuff)
            const uniqueResults = [];
            const seenIds = new Set();
            for (const p of results) {
                if (p && !seenIds.has(p.id)) {
                    uniqueResults.push(p);
                    seenIds.add(p.id);
                }
            }
            return uniqueResults;
        },
        enabled: likedProductIds.length > 0
    });

    const deleteInteraction = useMutation({
        mutationFn: (id) => apiClient.entities.UserInteraction.delete(id),
        onSuccess: () => {
            // Invalidate once per delete, acceptable
            queryClient.invalidateQueries({ queryKey: ['interactions'] });
        },
    });

    // likedProducts is now directly 'products' since we fetched exactly what we needed
    const likedProducts = products;

    const filteredProducts = likedProducts.filter(product => {
        // Search filter - fuzzy matching
        const matchesSearch = fuzzyMatchProduct(product, searchQuery);

        if (!matchesSearch) return false;

        // Apply advanced filters
        // Safe price check: Ensure price is treated as number
        const price = Number(product.price || 0);
        if (price < filters.minPrice || price > filters.maxPrice) return false;

        // Filter by subcategories
        if (filters.subcategories.length > 0) {
            const rawCat = product.subcategory || product.style;
            const normalized = normalizeCategory(rawCat);
            if (!filters.subcategories.includes(normalized)) return false;
        }

        // Filter by colors
        if (filters.colors && filters.colors.length > 0) {
            if (!product.colors || product.colors.length === 0) return false;
            const productColors = product.colors.map(c => normalizeColor(getColorName(c)));
            if (!productColors.some(c => filters.colors.includes(c))) return false;
        }

        if (filters.brands && filters.brands.length > 0 && !filters.brands.includes(product.brand)) return false;
        if (filters.sizes.length > 0 && !product.sizes?.some(s => filters.sizes.includes(s))) return false;
        if (filters.inStockOnly) {
            const hasAvailableSize = product.sizes && product.sizes.length > 0 && product.sizes.some(s => typeof s === 'string' && !s.includes('(Out of Stock)'));
            if (!hasAvailableSize) return false;
        }

        return true;
    });

    const handleToggleSelect = useCallback((product) => {
        const pId = String(product.id);
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            if (next.has(pId)) {
                next.delete(pId);
            } else {
                next.add(pId);
            }
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        if (selectedProductIds.size === filteredProducts.length) {
            setSelectedProductIds(new Set());
        } else {
            setSelectedProductIds(new Set(filteredProducts.map(p => String(p.id))));
        }
    }, [filteredProducts, selectedProductIds.size]);

    const handleBatchDelete = useCallback(async () => {
        if (selectedProductIds.size === 0) return;
        setIsBatchDeleting(true);
        try {
            const interactionsToDelete = interactions.filter(
                i => selectedProductIds.has(String(i.product_id)) && ['swipe_right', 'like'].includes(i.action)
            );
            if (interactionsToDelete.length > 0) {
                await Promise.all(interactionsToDelete.map(i => deleteInteraction.mutateAsync(i.id)));
            }
            // Enregistrer swipe_left pour l'algorithme
            await Promise.all(Array.from(selectedProductIds).map(id =>
                apiClient.entities.UserInteraction.create({ product_id: id, action: 'swipe_left', context: 'matches_batch_delete' }).catch(() => null)
            ));

            toast.success(`${selectedProductIds.size} match${selectedProductIds.size > 1 ? 'es' : ''} supprimé${selectedProductIds.size > 1 ? 's' : ''}`);
            setSelectedProductIds(new Set());
            setIsSelectionMode(false);
            setShowBatchDeleteDialog(false);
            queryClient.invalidateQueries({ queryKey: ['interactions'] });
            queryClient.invalidateQueries({ queryKey: ['interactions', 'likes'] });
        } catch (err) {
            console.error("Batch delete matches error:", err);
            toast.error("Erreur lors de la suppression des matches");
        } finally {
            setIsBatchDeleting(false);
        }
    }, [selectedProductIds, interactions, deleteInteraction, queryClient]);

    const handleCancelSelection = useCallback(() => {
        setIsSelectionMode(false);
        setSelectedProductIds(new Set());
    }, []);

    const handleDeselectAll = useCallback(() => {
        setSelectedProductIds(new Set());
    }, []);

    const handleDelete = (productId) => {
        // Find ALL interactions for this product to remove duplicates
        const productInteractions = interactions.filter(
            i => i.product_id === productId && ['swipe_right', 'like'].includes(i.action)
        );

        productInteractions.forEach(interaction => {
            deleteInteraction.mutate(interaction.id);
        });
        setProductToDelete(null);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen p-4 md:p-8" aria-busy="true" aria-label="Chargement des matches">
                <div className="max-w-7xl mx-auto">
                    {/* Header skeleton */}
                    <div className="mb-8">
                        <div className="skeleton-bone skeleton-stagger-1" style={{ height: '1.875rem', width: '10rem' }} />
                        <div className="skeleton-bone skeleton-stagger-2 mt-2" style={{ height: '0.875rem', width: '8rem' }} />
                    </div>
                    {/* Search + Filter skeleton */}
                    <div className="mb-6 flex gap-3 items-center">
                        <div className="skeleton-bone skeleton-rounded-lg skeleton-searchbar skeleton-stagger-2" />
                        <div className="skeleton-bone skeleton-rounded-lg skeleton-filter-btn skeleton-stagger-3" />
                    </div>
                    {/* Grid skeleton */}
                    <SkeletonGrid count={10} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-8 pb-28">
            <div className="max-w-7xl mx-auto">
                {/* Header with Title & Selection Controls */}
                <div className="mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mes Matches</h1>
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">
                                    {filteredProducts.length}
                                </span>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Retrouvez tous vos coups de cœur et gérez vos collections.
                            </p>
                        </div>

                        {/* Top Selection Activation Button */}
                        {filteredProducts.length > 0 && !isSelectionMode && (
                            <Button
                                variant="outline"
                                onClick={() => setIsSelectionMode(true)}
                                className="gap-2 h-10 border-gray-300 dark:border-gray-700 hover:border-amber-500 hover:text-amber-600 transition-colors shadow-xs"
                            >
                                <ListChecks className="w-4 h-4" />
                                Sélectionner
                            </Button>
                        )}
                    </div>

                    {/* Sticky Active Selection Toolbar that follows the user */}
                    <AnimatePresence>
                        {isSelectionMode && (
                            <m.div
                                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                                className="sticky top-3 z-30 mb-4 bg-amber-500/10 dark:bg-amber-950/40 backdrop-blur-xl border-2 border-amber-500/40 dark:border-amber-600/50 shadow-lg rounded-2xl p-3 flex flex-wrap items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-2.5 pl-1">
                                    <span className="flex h-3 w-3 rounded-full bg-amber-500 animate-pulse" />
                                    <div>
                                        <span className="text-sm font-bold text-amber-950 dark:text-amber-200">
                                            Mode sélection actif
                                        </span>
                                        <span className="text-xs text-amber-800/80 dark:text-amber-300/80 ml-2">
                                            ({selectedProductIds.size} sur {filteredProducts.length} sélectionné{selectedProductIds.size > 1 ? 's' : ''})
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSelectAll}
                                        className="text-xs h-8 gap-1.5 bg-white/80 dark:bg-zinc-800/80 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 hover:bg-amber-100/70"
                                    >
                                        <CheckCheck className="w-3.5 h-3.5" />
                                        {selectedProductIds.size === filteredProducts.length ? "Tout désélectionner" : "Tout sélectionner"}
                                    </Button>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={handleCancelSelection}
                                        className="text-xs h-8 gap-1 bg-gray-900 dark:bg-zinc-700 hover:bg-gray-800 text-white shadow-xs"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                        Annuler
                                    </Button>
                                </div>
                            </m.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Collections / Categories Quick Navigation Pills */}
                {uniqueCategoryNames.length > 0 && (
                    <div className="mb-6 flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap mr-1">
                            Collections :
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-600 text-white shadow-sm">
                                <Heart className="w-3.5 h-3.5 fill-current" />
                                Tous mes matches
                            </span>
                            {uniqueCategoryNames.map(catName => {
                                const count = userCategories.filter(c => c.category_name === catName).length;
                                return (
                                    <Link
                                        key={catName}
                                        to={`/categories?name=${encodeURIComponent(catName)}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white dark:bg-zinc-800/80 border border-gray-200 dark:border-zinc-700/80 text-gray-700 dark:text-gray-300 hover:border-amber-500 hover:text-amber-600 dark:hover:text-amber-400 transition-colors shadow-xs whitespace-nowrap"
                                    >
                                        <Tag className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                        {catName}
                                        <span className="text-[10px] px-1.5 py-0.2 bg-black/5 dark:bg-white/10 rounded-full font-semibold">
                                            {count}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Search and Filter */}
                <div className="mb-6 flex gap-3 items-center">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                            placeholder="Rechercher dans vos matches..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 h-10"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-4 h-4 text-gray-500" />
                            </button>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(true)}
                        className="gap-2 h-10"
                    >
                        <Filter className="w-4 h-4" />
                        Filtres
                    </Button>
                </div>

                {/* Matches Grid */}
                {filteredProducts.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center max-w-md">
                            <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-950/30 dark:to-yellow-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Heart className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                {searchQuery || filters.minPrice > 0 || filters.maxPrice < 500 || filters.subcategories.length > 0 ? "Aucun résultat" : "Aucun match pour le moment"}
                            </h2>
                            <p className="text-gray-600">
                                {searchQuery || filters.minPrice > 0 || filters.maxPrice < 500 || filters.subcategories.length > 0
                                    ? "Essayez d'autres mots-clés ou ajustez vos filtres"
                                    : "Commencez à swiper pour trouver vos coups de cœur !"}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-6">
                        <AnimatePresence>
                            {filteredProducts.map(product => (
                                <MatchCard
                                    key={product.id}
                                    product={product}
                                    onDelete={(id) => setProductToDelete(id)}
                                    source="matches"
                                    isSelectable={isSelectionMode}
                                    isSelected={selectedProductIds.has(String(product.id))}
                                    onToggleSelect={() => handleToggleSelect(product)}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Barre flottante d'actions pour la sélection multiple qui suit l'utilisateur - Centrée sur la zone de page (excluant la sidebar) */}
            <AnimatePresence>
                {isSelectionMode && (
                    <div className="fixed bottom-6 inset-x-0 md:left-64 z-50 flex justify-center pointer-events-none px-4">
                        <m.div
                            initial={{ opacity: 0, y: 30, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 30, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 350, damping: 25 }}
                            className="pointer-events-auto max-w-lg w-full bg-gray-950/95 dark:bg-zinc-900/95 text-white backdrop-blur-xl border border-white/15 dark:border-zinc-800 shadow-2xl rounded-2xl p-3 sm:px-4 sm:py-3 flex items-center justify-between gap-3"
                        >
                            <div className="flex items-center gap-2 pl-1 min-w-0">
                                <span className="flex h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                                <span className="text-sm font-semibold truncate">
                                    {selectedProductIds.size > 0
                                        ? `${selectedProductIds.size} match${selectedProductIds.size > 1 ? 'es' : ''} sélectionné${selectedProductIds.size > 1 ? 's' : ''}`
                                        : "Touchez un vêtement pour le sélectionner"
                                    }
                                </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {selectedProductIds.size > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleDeselectAll}
                                        className="text-xs text-gray-300 hover:text-white hover:bg-white/10 h-8 px-2.5"
                                    >
                                        Désélectionner tout
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCancelSelection}
                                    className="text-xs text-gray-300 hover:text-white hover:bg-white/10 h-8 px-2.5"
                                >
                                    Annuler
                                </Button>
                                {selectedProductIds.size > 0 && (
                                    <Button
                                        size="sm"
                                        onClick={() => setShowBatchDeleteDialog(true)}
                                        className="bg-red-500 hover:bg-red-600 text-white font-medium text-xs h-8 px-3 gap-1.5 shadow-lg shadow-red-500/25 transition-all active:scale-95"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Supprimer ({selectedProductIds.size})
                                    </Button>
                                )}
                            </div>
                        </m.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Single Delete Confirmation Dialog */}
            <AlertDialog open={!!productToDelete} onOpenChange={() => setProductToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce match ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Ce produit sera retiré de vos matches. Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => handleDelete(productToDelete)}
                            className="bg-red-500 hover:bg-red-600"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Batch Delete Confirmation Dialog */}
            <AlertDialog open={showBatchDeleteDialog} onOpenChange={setShowBatchDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Supprimer {selectedProductIds.size} match{selectedProductIds.size > 1 ? 'es' : ''} ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Ces {selectedProductIds.size} articles seront définitivement retirés de vos matches et coups de cœur.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isBatchDeleting}>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleBatchDelete}
                            disabled={isBatchDeleting}
                            className="bg-red-500 hover:bg-red-600 gap-1.5"
                        >
                            <Trash2 className="w-4 h-4" />
                            {isBatchDeleting ? "Suppression en cours..." : `Supprimer (${selectedProductIds.size})`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Filter Panel Dialog */}
            <Dialog open={showFilters} onOpenChange={setShowFilters}>
                <DialogContent className="sm:max-w-md p-0">
                    <FilterPanel
                        filters={filters}
                        setFilters={setFilters}
                        onClose={() => setShowFilters(false)}
                        products={likedProducts}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}